/**
 * Triage page — the vendor-side CRA workflow.
 *
 * A VulnerabilityEvent is created by the KEV scan whenever an actively exploited
 * CVE appears in a KomponenteVersion that ships inside one of our products. It is
 * assessed bottom-up (DEV-100): the component verdict proposes the verdict of
 * every product version containing it, a confirmed product verdict AFFECTED
 * fans out Benachrichtigung cards for the instances running that version, and
 * each instance can be refined with environment factors and a priority. Only
 * those cards can be stamped as sent — that chain is the evidence trail for the
 * market surveillance authority.
 *
 * Contract details that shape the code below:
 *   - Every timestamp in this payload is a RAW `CmdbDateTime` (ISO,
 *     'dd/MM/yyyy HH:mm:ss' or epoch millis). All of them go through
 *     `fmtDate`/`fmtDay`; `new Date(x)` alone would render 'Invalid Date'.
 *   - `cve.base_score` may be a string — compared only via `parseScore`.
 *   - One row per component hit; `assessment` carries the progress of the
 *     product and instance levels. The dialog loads all three levels itself.
 *   - After a verdict or notification mutation the affected row is REFETCHED
 *     from the queue and patched into state. Advisory drafts and evidence belong
 *     to the product assessment and use its endpoints in the dialog.
 *   - Mutating controls are gated on `triage.write` / `notification.send`. An
 *     auditor sees every row, the full audit trail and every draft, and no
 *     enabled action.
 */

import type { MessageDescriptor } from '@lingui/core';
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, plural, t as translate } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import CveLink from '../components/CveLink';
import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import CvssScore from '../components/CvssScore';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchVulnerabilityEvents } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, fmtDate, fmtDay, fmtValue, truncate } from '../lib/format';
import { intlLocale } from '../lib/locale';
import { missingPermissionTitle, readOnlyNotice } from '../lib/permissions';
import type {
  Bewertung,
  EventAssessmentSummary,
  KpiCounter,
  ReportStage,
  VulnerabilityEventRow,
  VulnerabilityEventsResponse,
} from '../lib/types';

import AssessmentDialog, { type AssessmentTab } from './triage/AssessmentDialog';
import {
  EventAuditTrail,
  EventCveFacts,
  EventInstanzen,
  EventScopeFacts,
} from './triage/EventFacts';
import {
  BEWERTUNG_LABELS,
  BEWERTUNG_TONE,
  kundenOf,
  matchesSearch,
  parseScore,
  produktVersionLabel,
} from './triage/eventHelpers';

import './triage/triage.css';

/** How many customer names fit into a table cell before the '+N' overflow. */
const KUNDEN_VISIBLE = 3;

const SCORE_OPTIONS: ReadonlyArray<{ value: string; label: MessageDescriptor }> = [
  { value: '', label: msg`Min. CVSS: alle` },
  { value: '4', label: msg`≥ 4,0 (mittel)` },
  { value: '7', label: msg`≥ 7,0 (hoch)` },
  { value: '9', label: msg`≥ 9,0 (kritisch)` },
];

/**
 * Which assessments the queue shows: OFFEN = anything left to assess on any
 * level (DEV-136), another component verdict, MAIL = notification still to send.
 */
type BewertungFilter = Bewertung | 'MAIL' | 'ALLE';

interface Filters {
  bewertung: BewertungFilter;
  search: string;
  /** Empty string = no CVSS floor. Kept as a string because it comes from a <select>. */
  minScore: string;
  /** 'YYYY-MM-DD' — events whose KEV date is older are hidden. */
  kevFrom: string;
  onlyKevListed: boolean;
  /** Minimum number of distinct affected customers. */
  minKunden: string;
}

const INITIAL_FILTERS: Filters = {
  bewertung: 'OFFEN',
  search: '',
  minScore: '',
  kevFrom: '',
  onlyKevListed: false,
  minKunden: '',
};

/**
 * One select for assessment and the KEV-list flag. Every assessment is a
 * choice of its own; „KEV-gelistet“ combines with „Offen“ and „Alle“ as before.
 */
type StatusFilter =
  | 'open' | 'open-kev' | 'pruefung' | 'betroffen' | 'mail' | 'nicht' | 'behoben' | 'all' | 'kev';

const STATUS_FILTERS: Readonly<Record<StatusFilter, { label: MessageDescriptor; flags: Pick<Filters, 'bewertung' | 'onlyKevListed'> }>> = {
  open: { label: msg`Nur offene`, flags: { bewertung: 'OFFEN', onlyKevListed: false } },
  'open-kev': { label: msg`Offene, KEV-gelistet`, flags: { bewertung: 'OFFEN', onlyKevListed: true } },
  pruefung: { label: msg`In Prüfung`, flags: { bewertung: 'UNDER_INVESTIGATION', onlyKevListed: false } },
  betroffen: { label: msg`Betroffen`, flags: { bewertung: 'BETROFFEN', onlyKevListed: false } },
  mail: { label: msg`Benachrichtigung offen`, flags: { bewertung: 'MAIL', onlyKevListed: false } },
  nicht: { label: msg`Nicht betroffen`, flags: { bewertung: 'NICHT_BETROFFEN', onlyKevListed: false } },
  behoben: { label: msg`Vom Scan geschlossen`, flags: { bewertung: 'BEHOBEN', onlyKevListed: false } },
  all: { label: msg`Alle Bewertungen`, flags: { bewertung: 'ALLE', onlyKevListed: false } },
  kev: { label: msg`Alle, KEV-gelistet`, flags: { bewertung: 'ALLE', onlyKevListed: true } },
};

function sameFilters(a: Filters, b: Filters): boolean {
  return (Object.keys(a) as (keyof Filters)[]).every((key) => a[key] === b[key]);
}

function statusFilterValue(filters: Filters): StatusFilter {
  const keys = Object.keys(STATUS_FILTERS) as StatusFilter[];
  return (
    keys.find((key) => {
      const flags = STATUS_FILTERS[key].flags;
      return flags.bewertung === filters.bewertung && flags.onlyKevListed === filters.onlyKevListed;
    }) ??
    keys.find((key) => STATUS_FILTERS[key].flags.bewertung === filters.bewertung) ??
    'all'
  );
}

/** A level above the component is marked for review or contradicts sent notifications. */
function needsReview(event: VulnerabilityEventRow): boolean {
  const a = event.assessment;
  return a != null && (a.products.needs_review + a.instances.needs_review + a.products.contradictions) > 0;
}

/** Product versions containing the component without a confirmed verdict. */
function unconfirmedProducts(event: VulnerabilityEventRow): number {
  const a = event.assessment;
  return a == null ? 0 : a.products.total - a.products.confirmed;
}

/** The component verdict is still to be made (open or under investigation). */
function componentOpen(event: VulnerabilityEventRow): boolean {
  return event.bewertung === 'OFFEN' || event.bewertung === 'UNDER_INVESTIGATION';
}

/**
 * Anything left to assess, on whichever level (DEV-136) — the same rule the
 * server counts `offen` with. A hit the KEV scan closed is done.
 */
function needsAssessment(event: VulnerabilityEventRow): boolean {
  if (event.bewertung === 'BEHOBEN') return false;
  return componentOpen(event) || unconfirmedProducts(event) > 0 || needsReview(event);
}

/** What the row's button does next, and which tab of the dialog it opens. */
function nextStep(event: VulnerabilityEventRow): { label: string; tab: AssessmentTab } | null {
  if (event.bewertung === 'BEHOBEN') return null;
  if (componentOpen(event)) return { label: translate`Komponente bewerten`, tab: 'component' };
  const unconfirmed = unconfirmedProducts(event);
  if (unconfirmed > 0) {
    return {
      label: translate`Vorschlag für ${plural(unconfirmed, { one: '# Produktversion', other: '# Produktversionen' })} bestätigen / bearbeiten`,
      tab: 'products',
    };
  }
  return needsReview(event) ? { label: translate`Erneut prüfen`, tab: 'products' } : null;
}

function matchesBewertung(event: VulnerabilityEventRow, bewertung: BewertungFilter): boolean {
  if (bewertung === 'ALLE') return true;
  if (bewertung === 'MAIL') return event.benachrichtigungen_offen > 0;
  if (bewertung === 'OFFEN') return needsAssessment(event);
  return event.bewertung === bewertung;
}

function sumBy(
  events: readonly VulnerabilityEventRow[],
  pick: (event: VulnerabilityEventRow) => number,
): number {
  return events.reduce((total, event) => total + pick(event), 0);
}

/** Parses a filter input that may be empty or junk into a numeric floor. */
function numericFloor(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Current native KEV state; unlike the CVE card flag this is not persisted. */
function hasNativeKevAssertion(event: VulnerabilityEventRow): boolean {
  return event.kev.sources.some((source) => Boolean(source?.trim()));
}

/**
 * Event, product version and dialog tab of a `?event=&version=&tab=` link;
 * null for missing or junk values. `tab=case` opens the vulnerability case
 * (from the page „Meldefristen“, DEV-137).
 */
const REPORT_STAGES: ReadonlySet<string> = new Set(['early_warning', 'notification', 'final_report']);

function deepLinkOf(params: URLSearchParams): {
  eventId: number | null; versionId: number | null; tab: AssessmentTab | null; report: ReportStage | null;
} {
  const id = (name: string): number | null => {
    const value = Number(params.get(name));
    return Number.isInteger(value) && value > 0 ? value : null;
  };
  const report = params.get('report') ?? '';
  return {
    eventId: id('event'), versionId: id('version'), tab: params.get('tab') === 'case' ? 'case' : null,
    report: REPORT_STAGES.has(report) ? report as ReportStage : null,
  };
}

/** Products with a confirmed AFFECTED whose case advisory is not released yet (DEV-134). */
function AdvisoryOpen({ count }: Readonly<{ count: number | undefined }>): ReactNode {
  const { t } = useLingui();
  if (!count) return null;
  return (
    <div className="prod">
      <Chip tone="warn" title={t`Kundenmails werden erst mit dem freigegebenen Security Advisory eingereiht`}>
        {t`${plural(count, { one: 'Advisory nicht freigegeben', other: '# Advisories nicht freigegeben' })}`}
      </Chip>
    </div>
  );
}

/** Instances follow their product version; only deviations and reviews are worth a mention (DEV-130). */
function InstanceDeviations({ instances }: Readonly<{ instances: EventAssessmentSummary['instances'] }>): ReactNode {
  const { t } = useLingui();
  const parts = [
    instances.deviations ? t`${plural(instances.deviations, { one: '# Instanz-Abweichung', other: '# Instanz-Abweichungen' })}` : null,
    instances.needs_review ? t`${plural(instances.needs_review, { one: '# Instanz erneut prüfen', other: '# Instanzen erneut prüfen' })}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? <>{` · ${parts.join(' · ')}`}</> : null;
}

/** Identifies a KPI tile independently of its translated label. */
type TileKey = 'open' | 'mail' | 'events' | 'affected' | 'not-affected' | 'closed';

export function TriagePage(): ReactNode {
  const { t, i18n } = useLingui();
  const { user, has } = useAuth();
  const toast = useToast();

  const [data, setData] = useState<VulnerabilityEventsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [searchParams] = useSearchParams();
  // `?q=` deep links (from the Audit-Log) open the queue on one case, decided
  // or not — so the 'Nur offene' default must not hide it.
  const [filters, setFilters] = useState<Filters>(() => {
    const q = searchParams.get('q')?.trim() ?? '';
    return q === '' ? INITIAL_FILTERS : { ...INITIAL_FILTERS, bewertung: 'ALLE', search: q };
  });
  // `?event=&version=` deep links (from the Benachrichtigungen page) open the
  // dialog on one product version; with `tab=case` on the case of its product,
  // where the Security Advisory is written (DEV-134).
  const [linked] = useState(() => deepLinkOf(searchParams));
  const [bewertungEventId, setBewertungEventId] = useState<number | null>(linked.eventId);
  const [assessmentTab, setAssessmentTab] = useState<AssessmentTab>(
    linked.tab ?? (linked.versionId ? 'products' : 'component'));
  const [linkedVersionId, setLinkedVersionId] = useState<number | null>(linked.versionId);
  // `report=` opens the authority report of a stage in the case tab (DEV-60).
  const [linkedReport, setLinkedReport] = useState<ReportStage | null>(linked.report);

  // Guards a late response from overwriting a newer one.
  const requestSeq = useRef(0);

  const canTriage = has('triage.write');
  const canSend = has('notification.send');
  const readOnly = !canTriage && !canSend;
  const bewerter = user?.name?.trim() || user?.email || t`unbekannt`;

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await fetchVulnerabilityEvents();
      if (seq !== requestSeq.current) return;
      setData(response);
      setLoadError(null);
      setLoadedAt(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Refetch after a mutation and patch exactly the affected row back into state.
   * There is no per-event GET, so the list is the source; patching one row keeps
   * the other rows, the sort order and the expanded details stable while still
   * showing only server-confirmed values.
   */
  const refreshEvent = useCallback(async (eventId: number): Promise<void> => {
    const seq = ++requestSeq.current;
    const fresh = await fetchVulnerabilityEvents();
    // A full reload started meanwhile owns the state — drop this result.
    if (seq !== requestSeq.current) return;
    setData((current) => {
      if (current === null) return fresh;
      const updated = fresh.events.find((event) => event.id === eventId);
      const events =
        updated === undefined
          ? current.events.filter((event) => event.id !== eventId)
          : current.events.map((event) => (event.id === eventId ? updated : event));
      return { count: fresh.count, offen: fresh.offen, events };
    });
    setLoadedAt(new Date());
  }, []);

  // Memoised so the derived lists below only recompute when the payload changes.
  const events = useMemo<readonly VulnerabilityEventRow[]>(() => data?.events ?? [], [data]);

  const bewertungEvent = useMemo(
    () => events.find((event) => event.id === bewertungEventId) ?? null,
    [events, bewertungEventId],
  );
  const handleChanged = useCallback((): void => {
    if (bewertungEventId === null) return;
    void refreshEvent(bewertungEventId).catch((err: unknown) => {
      const message = errorMessage(err);
      toast.error(t`Zeile konnte nicht neu geladen werden: ${message}`);
    });
  }, [bewertungEventId, refreshEvent, toast, t]);

  const counters = useMemo<Array<KpiCounter & { key: TileKey }>>(() => {
    const betroffen = events.filter((event) => event.bewertung === 'BETROFFEN').length;
    const nichtBetroffen = events.filter((event) => event.bewertung === 'NICHT_BETROFFEN').length;
    const behoben = events.filter((event) => event.bewertung === 'BEHOBEN').length;
    const offeneMails = sumBy(events, (event) => event.benachrichtigungen_offen);
    const mitOffenerMail = events.filter((event) => event.benachrichtigungen_offen > 0).length;
    const versendeteMails = sumBy(events, (event) => event.benachrichtigungen_versendet);
    // Work first, then how the decided hits break down (DEV-136).
    return [
      {
        key: 'open',
        label: t`Offene Bewertungen`,
        value: data?.offen ?? 0,
        tone: (data?.offen ?? 0) > 0 ? 'warn' : 'good',
        title: t`Treffer, an denen noch etwas zu bewerten ist: Komponente, Produktversionen oder eine erneut zu prüfende Bewertung`,
      },
      {
        // Counted per event, like the other tiles, so its filter shows exactly
        // this many rows; the notification totals go in the tooltip.
        key: 'mail',
        label: t`Benachrichtigung offen`,
        value: mitOffenerMail,
        tone: mitOffenerMail > 0 ? 'warn' : 'good',
        title: t`${plural(offeneMails, { one: '# Benachrichtigung offen', other: '# Benachrichtigungen offen' })}, ${versendeteMails} bereits als versendet gestempelt`,
      },
      {
        key: 'events',
        label: t`Events`,
        value: data?.count ?? events.length,
        tone: 'neutral',
        title: t`KEV-Treffer in Komponentenversionen insgesamt`,
      },
      {
        key: 'affected',
        label: t`Betroffen`,
        value: betroffen,
        tone: betroffen > 0 ? 'alert' : 'good',
        title: t`Komponentenbewertung „Betroffen“ — Vorschlag für die Produktversionen`,
      },
      {
        key: 'not-affected',
        label: t`Nicht betroffen`,
        value: nichtBetroffen,
        tone: 'neutral',
        title: t`CVE als nicht anwendbar bewertet (mit Begründung im Audit-Trail)`,
      },
      {
        key: 'closed',
        label: t`Vom Scan geschlossen`,
        value: behoben,
        tone: 'neutral',
        title: t`Vom KEV-Scan automatisch geschlossen (Version nicht mehr im Feld oder CVE entfallen)`,
      },
    ];
  }, [data, events, t]);

  // Every tile maps onto the assessment filter: each opens its whole category
  // (other filters cleared) and stays marked while exactly it is active. A
  // second click goes back to the default (open ones); on „Offene“, which is
  // the default, it shows all.
  const kpiTiles = useMemo((): KpiItem[] => {
    const only = (bewertung: BewertungFilter): Filters => ({ ...INITIAL_FILTERS, bewertung });
    // Looked up by the tile key, never by the translated label.
    const targets: Readonly<Record<TileKey, Filters>> = {
      events: only('ALLE'),
      open: INITIAL_FILTERS,
      affected: only('BETROFFEN'),
      'not-affected': only('NICHT_BETROFFEN'),
      closed: only('BEHOBEN'),
      mail: only('MAIL'),
    };
    return counters.map((counter) => {
      const target = targets[counter.key];
      const active = sameFilters(filters, target);
      const cleared = target === INITIAL_FILTERS ? only('ALLE') : INITIAL_FILTERS;
      const { label, value } = counter;
      return {
        ...counter,
        ariaLabel: t`${label}: ${value} anzeigen`,
        selected: active,
        controls: 'triage-results',
        onClick: () => setFilters(active ? cleared : target),
      };
    });
  }, [counters, filters, t]);

  const visibleRows = useMemo<VulnerabilityEventRow[]>(() => {
    const scoreFloor = numericFloor(filters.minScore);
    const kundenFloor = numericFloor(filters.minKunden);
    return events.filter((event) => {
      if (!matchesBewertung(event, filters.bewertung)) return false;
      if (!matchesSearch(event, filters.search)) return false;
      if (scoreFloor !== null) {
        const score = parseScore(event.cve.base_score);
        // Events without a score are hidden as soon as a floor is demanded —
        // they cannot be proven to satisfy it.
        if (score === null || score < scoreFloor) return false;
      }
      if (filters.onlyKevListed && !hasNativeKevAssertion(event)) return false;
      if (filters.kevFrom !== '') {
        const added = event.kev.aufgenommen_am;
        // Both sides are 'YYYY-MM-DD', so a string compare is a date compare.
        if (added === null || added < filters.kevFrom) return false;
      }
      if (kundenFloor !== null && event.betroffene_kunden < kundenFloor) return false;
      return true;
    });
  }, [events, filters]);

  const columns = useMemo<DataTableColumn<VulnerabilityEventRow>[]>(() => {
    /** Everyone with queue access may open the levels; saving needs triage.write. */
    function renderActions(event: VulnerabilityEventRow): ReactNode {
      const step = nextStep(event);
      const open = step !== null;
      let label = t`Öffnen`;
      if (!canTriage) label = t`Ansehen`;
      else if (step !== null) label = step.label;
      return (
        <div className="triage-actions">
          <button
            type="button"
            className={canTriage && open ? 'primary small' : 'ghost small'}
            title={canTriage ? undefined : missingPermissionTitle('triage.write')}
            onClick={(clickEvent) => {
              // The row itself toggles the detail area — do not fold it here.
              clickEvent.stopPropagation();
              setAssessmentTab(step?.tab ?? (componentOpen(event) ? 'component' : 'products'));
              setBewertungEventId(event.id);
            }}
          >
            {label}
          </button>
        </div>
      );
    }

    return [
      {
        key: 'cve',
        header: 'CVE',
        width: '16%',
        sortValue: (event) => event.cve.id,
        render: (event) => (
          <div className="triage-cve">
            <ChipList>
              <CveLink cve={event.cve.id} />
              {event.cve.aktiv_ausgenutzt ? (
                <Chip tone="warn" title={t`Aktiv ausgenutzt`}>
                  <Trans>aktiv</Trans>
                </Chip>
              ) : null}
            </ChipList>
            {event.cve.beschreibung ? (
              <div className="prod" title={event.cve.beschreibung}>
                {truncate(event.cve.beschreibung, 70)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'score',
        header: 'CVSS',
        align: 'right',
        width: '7%',
        headerTitle: t`CVSS Base Score`,
        sortValue: (event) => parseScore(event.cve.base_score),
        render: (event) => <CvssScore score={parseScore(event.cve.base_score)} />,
      },
      {
        key: 'kev',
        header: t`KEV seit`,
        width: '10%',
        headerTitle: t`Publikationsdatum der bevorzugten nativen KEV-Assertion`,
        sortValue: (event) => event.kev.aufgenommen_am,
        render: (event) => {
          if (event.kev.aufgenommen_am === null) {
            return hasNativeKevAssertion(event)
              ? <Chip tone="alert" title={t`KEV-Assertion ohne Publikationsdatum`}><Trans>gelistet</Trans></Chip>
              : <span className="muted" title={t`Keine KEV-Assertion oder Abruf fehlgeschlagen`}>{DASH}</span>;
          }
          const deadline = event.kev.frist ? fmtDay(event.kev.frist) : null;
          return (
            <div>
              {fmtDay(event.kev.aufgenommen_am)}
              {deadline ? <div className="prod"><Trans>Frist {deadline}</Trans></div> : null}
              {event.kev.ransomware === 'Known' ? (
                <div className="prod triage-ransom"><Trans>Ransomware bekannt</Trans></div>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'komponente',
        header: t`Komponente / Produkte`,
        sortValue: (event) => event.komponente.name,
        render: (event) => {
          const versions = event.produktversionen.map(produktVersionLabel).join(', ');
          return (
          <div>
            <div className="kunde-name">
              {fmtValue(event.komponente.name)}{' '}
              <span className="muted">{event.komponente.version ?? ''}</span>
            </div>
            <div className="prod">
              {event.komponente.hersteller ? `${event.komponente.hersteller} · ` : ''}
              {event.produktversionen.length === 0
                ? t`in keiner Produktversion`
                : t`in ${versions}`}
            </div>
          </div>
          );
        },
      },
      {
        key: 'kunden',
        header: t`Betroffene Kunden`,
        width: '14%',
        headerTitle: t`Anzahl unterschiedlicher Kunden mit betroffenen Produktinstanzen`,
        sortValue: (event) => event.betroffene_kunden,
        render: (event) => {
          const kunden = kundenOf(event);
          if (kunden.length === 0) {
            return <span className="muted"><Trans>keine</Trans></span>;
          }
          const shown = kunden.slice(0, KUNDEN_VISIBLE);
          const hidden = kunden.slice(KUNDEN_VISIBLE);
          const hiddenNames = hidden.map((kunde) => kunde.name ?? '?').join(', ');
          return (
            <div>
              <span className="kunde-name">
                {shown.map((kunde) => kunde.name ?? '?').join(', ')}
              </span>
              {hidden.length > 0 ? (
                <>
                  {' '}
                  <Chip title={t`Weitere Kunden: ${hiddenNames}`}>+{hidden.length}</Chip>
                </>
              ) : null}
              <div className="prod">
                <Plural value={event.betroffene.length} one="# Produktinstanz" other="# Produktinstanzen" />
              </div>
            </div>
          );
        },
      },
      {
        key: 'bewertung',
        header: t`Bewertung`,
        width: '13%',
        sortValue: (event) => event.bewertung,
        render: (event) => {
          const confirmed = event.assessment?.products.confirmed ?? 0;
          const total = event.assessment?.products.total ?? 0;
          return (
          <div>
            <ChipList>
              <Chip tone={BEWERTUNG_TONE[event.bewertung]} title={t`Komponentenbewertung`}>{BEWERTUNG_LABELS[event.bewertung]}</Chip>
              {needsReview(event) ? <Chip tone="warn"><Trans>Erneut prüfen</Trans></Chip> : null}
              {event.assessment?.products.contradictions ? <Chip tone="alert" title={t`Kunden wurden als betroffen benachrichtigt, die Produktbewertung lautet anders`}><Trans>Widerspruch</Trans></Chip> : null}
            </ChipList>
            {event.assessment ? (
              <div className="prod">
                <Trans>Produkte {confirmed}/{total} bestätigt</Trans>
                <InstanceDeviations instances={event.assessment.instances} />
              </div>
            ) : null}
            {event.bewertet_von || event.bewertet_am ? (
              <div className="prod">
                {fmtValue(event.bewertet_von)} · {fmtDate(event.bewertet_am)}
              </div>
            ) : null}
            {event.begruendung ? (
              <div className="prod triage-begruendung" title={event.begruendung}>
                „{truncate(event.begruendung, 60)}“
              </div>
            ) : null}
          </div>
          );
        },
      },
      {
        key: 'benachrichtigung',
        header: t`Benachrichtigung`,
        width: '11%',
        sortValue: (event) => event.benachrichtigungen_offen,
        render: (event) => {
          const versendet = event.benachrichtigungen_versendet;
          const offen = event.benachrichtigungen_offen;
          const missing = <AdvisoryOpen count={event.assessment?.products.advisory_open} />;
          if (versendet === 0 && offen === 0) {
            return event.assessment?.products.affected ? (
              <div>
                <span className="muted" title={t`Erst die bestätigte Produktbewertung „Betroffen“ legt Benachrichtigungen an`}><Trans>ausstehend</Trans></span>
                {missing}
              </div>
            ) : (
              <span className="muted">{DASH}</span>
            );
          }
          return (
            <div>
              <Chip tone="good"><Trans>{versendet} versendet</Trans></Chip>
              {offen > 0 ? <div className="prod"><Trans>{offen} ausstehend</Trans></div> : null}
              {missing}
            </div>
          );
        },
      },
      {
        key: 'aktion',
        header: t`Aktion`,
        width: '15%',
        render: renderActions,
      },
    ];
    // i18n.locale: the cells carry text of module helpers (labels, next step).
  }, [canTriage, t, i18n.locale]);

  if (loading && data === null) {
    return <PageSpinner text={t`Bewertungs-Warteschlange wird geladen…`} />;
  }

  const loadedTime = loadedAt?.toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });
  const totalLabel = t`${plural(events.length, { one: '# Bewertung', other: '# Bewertungen' })}`;
  const visibleCount = visibleRows.length;

  return (
    <>
      <PageHeader
        title={t`KEV-Bewertungen`}
        sub={loadedTime === undefined ? undefined : t`Stand ${loadedTime}`}
      />

      {loadError === null ? null : (
        <Banner kind="error" sub={t`Prüfen Sie die Verbindung zu CMDBuild.`}>
          {loadError}
        </Banner>
      )}

      {readOnly ? (
        <Banner kind="info">{readOnlyNotice('triage.write', 'notification.send')}</Banner>
      ) : null}

      <KpiRow counters={kpiTiles} />

      <section id="triage-results" className="card">
        <div className="card-head table-head">
          <h2 title={t`Standardsortierung: Treffer mit offener Bewertung zuerst, danach nach Event-ID absteigend.`}>
            <Trans>Warteschlange</Trans>
          </h2>
        </div>
        <div className="table-toolbar">
          <input
            type="search"
            className="triage-search"
            value={filters.search}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, search: changeEvent.target.value }))
            }
            placeholder={t`CVE, Komponente, Kunde suchen…`}
            aria-label={t`Warteschlange filtern`}
          />
          <select
            value={statusFilterValue(filters)}
            onChange={(changeEvent) =>
              setFilters((current) => ({
                ...current,
                ...STATUS_FILTERS[changeEvent.target.value as StatusFilter].flags,
              }))
            }
            aria-label={t`Nach Status filtern`}
          >
            {(Object.keys(STATUS_FILTERS) as StatusFilter[]).map((key) => (
              <option key={key} value={key}>
                {i18n._(STATUS_FILTERS[key].label)}
              </option>
            ))}
          </select>
          <select
            value={filters.minScore}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minScore: changeEvent.target.value }))
            }
            aria-label={t`Min. CVSS`}
          >
            {SCORE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {i18n._(option.label)}
              </option>
            ))}
          </select>
          <label className="triage-inline-field">
            <span><Trans>KEV ab</Trans></span>
            <input
              type="date"
              value={filters.kevFrom}
              onChange={(changeEvent) =>
                setFilters((current) => ({ ...current, kevFrom: changeEvent.target.value }))
              }
              aria-label={t`KEV-Aufnahme ab`}
            />
          </label>
          <input
            type="number"
            className="triage-min-kunden"
            min={0}
            step={1}
            value={filters.minKunden}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minKunden: changeEvent.target.value }))
            }
            placeholder={t`Min. Kunden`}
            aria-label={t`Min. Kunden`}
            title={t`Mindestanzahl betroffener Kunden`}
          />
          <div className="table-toolbar-options">
            {filters.search !== INITIAL_FILTERS.search ||
            filters.bewertung !== INITIAL_FILTERS.bewertung ||
            filters.onlyKevListed !== INITIAL_FILTERS.onlyKevListed ||
            filters.minScore !== INITIAL_FILTERS.minScore ||
            filters.kevFrom !== INITIAL_FILTERS.kevFrom ||
            filters.minKunden !== INITIAL_FILTERS.minKunden ? (
              <button type="button" className="link" onClick={() => setFilters(INITIAL_FILTERS)}>
                <Trans>Filter zurücksetzen</Trans>
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {visibleCount === events.length ? totalLabel : t`${visibleCount} von ${totalLabel}`}
            </span>
          </div>
        </div>

        {events.length === 0 && loadError === null ? (
          <EmptyState title={t`Noch keine KEV-Bewertungen vorhanden`}>
            <Trans>Bewertungen entstehen nach dem KEV-Abgleich für betroffene ausgelieferte Komponenten.</Trans>
          </EmptyState>
        ) : (
          <DataTable
            rows={visibleRows}
            columns={columns}
            rowKey={(event) => event.id}
            mobileBreakpoint={960}
            pageSize={10}
            paginationResetKey={JSON.stringify(filters)}
            resetPageOnRowsChange={false}
            revealRowKey={bewertungEventId}
            caption={t`Bewertungs-Warteschlange der KEV-Treffer`}
            emptyText={t`Keine Bewertungen für die aktuelle Auswahl. Filter anpassen oder im Statusfilter „Alle Bewertungen“ wählen.`}
            rowClassName={(event) => (needsAssessment(event) ? 'triage-row-open' : undefined)}
            renderDetail={(event) => (
              <div className="triage-detail">
                <EventCveFacts event={event} />
                <EventScopeFacts event={event} />
                <EventAuditTrail event={event} />
                <EventInstanzen event={event} />
              </div>
            )}
          />
        )}
      </section>

      {bewertungEvent === null ? null : (
        <AssessmentDialog
          key={bewertungEvent.id}
          event={bewertungEvent}
          bewerter={bewerter}
          canWrite={canTriage}
          initialTab={assessmentTab}
          initialVersionId={linkedVersionId}
          initialReport={linkedReport}
          onClose={() => {
            setBewertungEventId(null);
            setLinkedVersionId(null);
            setLinkedReport(null);
          }}
          onChanged={handleChanged}
        />
      )}
    </>
  );
}

export default TriagePage;
